import { useEffect, useState } from 'react';

const COMMIT_MS = 500;   // how often the snapshot is pushed to state (decoupled from the paint rate)

/**
 * useCanvasInfo — the WebCodecs analog of useVideoInfo for the cog "Camera Info overlay". The dragon
 * playback path renders to a <canvas> (no <video> element), so its stream stats come from the active
 * PlaybackPlayer's getStreamInfo() (decoded resolution + rolling presented-frame fps) rather than a
 * <video>'s videoWidth/rVFC. Returns the same shape useVideoInfo does, so formatVideoInfo/Cell are
 * product-agnostic — the only difference is where the numbers are read.
 *
 * Mirrors useVideoInfo's discipline: does NO work when disabled, and snapshots on a COMMIT_MS interval
 * (not per frame) so it costs one small poll while the overlay is on and nothing when it's off.
 *
 * @param {function} getPlayer  returns the currently-active PlaybackPlayer (from useSlotWebCodecs), or null
 * @param {object} opts
 * @param {boolean} opts.enabled  the global cog toggle AND this cell has a clip
 * @param {number} opts.profile   the transcode profile hint (kept for parity with useVideoInfo)
 */
export const useCanvasInfo = (getPlayer, { enabled = false, profile = 1 } = {}) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!enabled || typeof getPlayer !== 'function') { setInfo(null); return undefined; }
    let cancelled = false;

    const commit = () => {
      if (cancelled) return;
      const player = getPlayer();
      const stats = player && typeof player.getStreamInfo === 'function' ? player.getStreamInfo() : null;
      if (!stats) { setInfo(null); return; }
      const { width = 0, height = 0, fps = null, profile: livePro = null } = stats;
      setInfo({
        width,
        height,
        // A painting canvas is fully buffered (the whole clip is streamed into memory); map to
        // HAVE_ENOUGH_DATA once we have a decoded frame so the buffer line reads "ready", 0 before.
        readyState: width > 0 ? 4 : 0,
        // Prefer the profile the player is ACTUALLY decoding (cloud pro1/pro2, reflecting a fallback);
        // fall back to the caller's hint for the local/pbserver path where the player carries none.
        profile: livePro != null ? livePro : profile,
        fps,
      });
    };
    commit();                                  // immediate first snapshot
    const timer = setInterval(commit, COMMIT_MS);

    return () => { cancelled = true; clearInterval(timer); };
  }, [getPlayer, enabled, profile]);

  return enabled ? info : null;
};

export default useCanvasInfo;
